import { AutocompleteField } from "@models/autocompleteField";
import ProfileSituationService from "@services/profile-situation-service";
import { getSingleInstanceByElement, getSingleInstanceByName } from "../../../core";
import Component from "../../component.abstract";
import { notifyFromAlert } from "../../helpers/notifs";
import SuggestInput from "../../inputs/autocomplete/autocomplete";
import SimpleSelect from "../../inputs/simple-select/simple-select";
import Selector from "../../multiselect/select";
import SimpleProgress from "../../simple-progress/simple-progress";
import ProfileInformations from "../informations/profile-informations";
import { SituationDataFields } from "./situation-data-fields";

interface ProfileSituationProps {
    url: string;
}

export default class ProfileSituation extends Component {
    private url: string;
    private profileInformations: ProfileInformations;
    private saveButton: HTMLElement;
    private jobInput: SuggestInput;
    private qualificationSelect: SimpleSelect;
    private sectorSelect: Selector;
    private salarySelect: SimpleSelect;
    private data: any;
    private previousData: SituationDataFields;

    constructor(private props: ProfileSituationProps, element: HTMLElement) {
          super(props, element);
        this.props.url ? this.url = this.props.url : undefined;
    }
    mounted(): void {
         this.injectTemplate();
         this.profileInformations = getSingleInstanceByName("ProfileInformations");
        this.saveButton = this.element.querySelector(".profile-form-save");
        this.jobInput = getSingleInstanceByElement("SuggestInput", this.element.querySelector("input[data-autocomplete][name=currentJob]"));
        this.qualificationSelect = getSingleInstanceByElement("SimpleSelect", this.element.querySelector("#qualification"));
        this.sectorSelect = getSingleInstanceByElement("Selector", this.element.querySelector("#sector"));
        this.salarySelect = getSingleInstanceByElement("SimpleSelect", this.element.querySelector("#salary"));
        this.element.querySelector(".profile-form-save").addEventListener("click", () => {
            this.save();
        });

        if (this.url) {
            this.call();
        }
    }
    call():void {
        const service = new ProfileSituationService(this.url);

        service.setErrorHandler((error:any) => {
           let message: string;
           if(error?.Message) {
               message = error.Message;
           }
           this.displayError(message)
        });

        service.get((response:any) => {
            this.data = response;
            this.setFields();
            //Add tracking on load
            if (this.data.trackingsLoad && this.data.trackingsLoad.length) {
                const trackingsLoad = this.data.trackingsLoad[0]
                window.dataLayer.push(trackingsLoad)
            }
        });
    }
    createRadio(name:string):void {
        const div = this.element.querySelector("#" + name); 
        if (!div) {
            return;
        }
        div.innerHTML = "";
        const radioName = "radio_" + name;
        for (let i = 0; i < this.data[name].length; i++) {
            const option = this.data[name][i];
            const id = radioName + "_" + option.uri;

            const input = document.createElement("input");
            input.setAttribute("type", "radio");
            input.setAttribute("name", radioName);
            input.setAttribute("id", id);
            input.setAttribute("value",  option.uri);
            input.classList.add("radio");
            input.checked = !!option.selected;
            div.appendChild(input);

            const label = document.createElement("label");
            label.setAttribute("for", id);
            label.innerText = option.label;
            div.appendChild(label);
        }
    }
    setFields():void {
       if (this.data.currentJob) {
            this.jobInput.selectAuto({
                "id": this.data.currentJob.uri,
                "value": this.data.currentJob.label
            });
        }
        if (typeof this.data.isCurrentlyEmployed === "boolean") {
            const employedInputValue = this.data.isCurrentlyEmployed ? "yes" : "no";
            this.element.querySelector<HTMLInputElement>(`input[name=radio-situation][value=${employedInputValue}]`).checked = true;
        }
        
        this.createRadio("situationStatusOptions");

        if (typeof this.data.isHandicaped === "boolean") {
            const handicappedInputValue = this.data.isHandicaped ? "yes" : "no";
            this.element.querySelector<HTMLInputElement>(`input[name=radio-handicapped][value=${handicappedInputValue}]`).checked = true;
        }

        // Update select values
        this.qualificationSelect.updateItems(this.data.qualificationLevelOptions);
        this.salarySelect.updateItems(this.data.salaryOptions);

        if(this.previousData === undefined) {
            for (let i = 0; i < this.data.sectorOptions.length; i++) {
                const o = this.data.sectorOptions[i];
                const option = document.createElement("option");
                option.setAttribute("value", o.uri);
                option.innerHTML = o.label;
                this.sectorSelect.getElement().appendChild(option);
            }
        }
        this.sectorSelect.reloadOptions();

        // Set selected values
        for (let i = 0; i < this.data.qualificationLevelOptions.length; i++) {
            const option = this.data.qualificationLevelOptions[i];
            if (option.selected) {
                this.qualificationSelect.selectItem(option.uri);
                break;
            }
        }
        for (let i = 0; i < this.data.sectorOptions.length; i++) {
            const option = this.data.sectorOptions[i];
            if (option.selected) {
                const element = this.sectorSelect.getElement().querySelector('option[value="' + option.uri + '"]');
                if (element) {
                    this.sectorSelect.selectOption(element);
                }
            }
        }
        for (let i = 0; i < this.data.salaryOptions.length; i++) {
            const option = this.data.salaryOptions[i];
            if (option.selected) {
                this.salarySelect.selectItem(option.uri);
                break;
            }
        }

        this.createRadio("experienceOptions");
        this.createRadio("availabilityOptions");

        this.previousData = this.getFields();
        this.element.classList.remove('pending');
    }
    getFields(): SituationDataFields {
        let availability: AutocompleteField | null = null,
            experience: AutocompleteField | null = null,
            situationStatus: AutocompleteField | null = null;
        
        const radioAvailability = this.element.querySelector<HTMLInputElement>("input[name=radio_availabilityOptions]:checked");
         if (radioAvailability) {
            availability = {
                "label": this.element.querySelector<HTMLElement>("label[for='" + radioAvailability.getAttribute("id") +"']").innerText,
                "uri": radioAvailability.value,
                isValid() {
                    return !!this.uri;
                }
            }
        }
        const radioExperience = this.element.querySelector<HTMLInputElement>("input[name=radio_experienceOptions]:checked");
        if (radioExperience) {
            experience = {
                "label": this.element.querySelector<HTMLElement>("label[for='" + radioExperience.getAttribute("id") +"']").innerText,
                "uri": radioExperience.value,
                isValid() {
                    return !!this.uri;
                }
            };
        }
        
        const radiosituationStatus = this.element.querySelector<HTMLInputElement>("input[name=radio_situationStatusOptions]:checked");
        if (radiosituationStatus) {
            situationStatus = {
                "label": this.element.querySelector<HTMLElement>("label[for='" + radiosituationStatus.getAttribute("id") +"']").innerText,
                "uri": radiosituationStatus.value,
                isValid() {
                    return !!this.uri;
                }
            }
        }

        const dataFields = new SituationDataFields() 
        dataFields.availability = availability;
        dataFields.isCurrentlyEmployed = this.getRadioValue("radio-situation");
        dataFields.situationStatus = situationStatus;
        dataFields.experience = experience;
        dataFields.isHandicaped = this.getRadioValue("radio-handicapped");
        dataFields.currentJob = {
            "label": this.jobInput.getValue().currentJob,
            "uri": this.jobInput.getValue().currentJob_autocomplete,
            isValid() {
                return !!this.uri;
            }
        };
        dataFields.qualificationLevel = this.qualificationSelect.getCriterionFormatValue();
        dataFields.sectors = this.sectorSelect.getCriterionFormatValue();
        dataFields.salary = this.salarySelect.getCriterionFormatValue();
        return dataFields;
    }
    getRadioValue(radio: string):boolean|null {
        let value = null;
        const radioElement = document.querySelector<HTMLInputElement>("input[name=" + radio + "]:checked");
         if (radioElement) {
            value = radioElement.value === "yes";
        }

        return value;
    }
    
    save():void {
        const data = this.getFields();
        const isValid = data.checkValidity();
        if (!isValid) {
            return this.displayError(data.getError());
        }
        const service = new ProfileSituationService(this.url);
        this.saveButton.classList.add("disabled");
        const progressButtonInstance: SimpleProgress = getSingleInstanceByElement("SimpleProgress", this.saveButton)
        progressButtonInstance.start();

        service.setErrorHandler((error:any) => {
            let message: string;
            if(error?.Message) {
                message = error.Message;
            }
            this.displayError(message)
            this.saveButton.classList.remove("disabled");
            progressButtonInstance.stop();
        });

        service.post(data.toJson(), (response:any) => {
            this.previousData = data;
            this.profileInformations.setData(response.infosProfil);
            this.profileInformations.displayResults();
            this.profileInformations.initSearchLabels();
            this.profileInformations.setCompletion();

            //Add tracking event on save actual situation and check if 
            const popin = document.querySelectorAll(".hasOk")
            const notifValid = document.querySelectorAll("data-notification")
            if (popin.length < 1 && notifValid.length < 1) {
                window.dataLayer.push(this.data.trackingsClick[0])
            }
            progressButtonInstance.stop();
            this.saveButton.classList.remove("disabled");                    
            notifyFromAlert({
                "Icon": 0,
                "Message": response.message,
                "AnalyticsTrackings": "",
                "sticky": true,
                "vanish": true
            }, true);
        });
    }
    displayError(message?: string):void {
        notifyFromAlert({
            "Icon": 2,
            "Message": message ? message : "Aïe, une erreur est survenue, rechargez la page et réessayez.",
            "AnalyticsTrackings": "",
            "sticky": true
        }, true);
    }
    isDirty(): boolean {
        return JSON.stringify(this.getFields().toJson()) !== JSON.stringify(this.previousData.toJson());
    }
}